import { db, storage } from '../firebase';
import { collection, addDoc, getDocs, query, where, deleteDoc, doc, Timestamp, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { auth } from '../firebase';

export interface Dossier {
  id?: string;
  nom: string;
  description?: string;
  parentId?: string | null;
  bienId: string;
  userId?: string;
  type: 'dossier';
  categorie: 'gestioncourante' | 'travaux';
  createdAt?: Date;
  updatedAt?: Date;
  icone?: string;
  couleur?: string;
}

export interface DocumentItem {
  id?: string;
  nom: string;
  url: string; // URL du document dans Firebase Storage
  dossierId: string | null;
  bienId: string;
  userId?: string;
  // Support des deux formats (mobile et web)
  mimeType?: string;
  fileType?: string; // Format mobile
  taille?: number;
  fileSize?: number; // Format mobile
  fileName?: string; // Format mobile
  createdAt?: Date;
  updatedAt?: Date;
  type: 'document';
  categorie?: 'gestioncourante' | 'travaux' | 'historiquebatiment';
  dateduDocument?: Date;
}

class DossiersService {
  private dossiersCollection = 'dossiers';
  private documentsCollection = 'documents';

  // Upload un fichier sur Firebase Storage et retourne l'URL
  async uploadFile(file: File, bienId: string, categorie: string): Promise<string> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }

      // Créer un nom de fichier unique avec timestamp
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const filePath = `documents/${categorie}/${fileName}`;

      // Créer une référence dans Firebase Storage
      const storageRef = ref(storage, filePath);

      // Upload le fichier
      const snapshot = await uploadBytes(storageRef, file);

      // Récupérer l'URL de téléchargement
      const downloadURL = await getDownloadURL(snapshot.ref);

      return downloadURL;
    } catch (error) {
      console.error('Erreur lors de l\'upload du fichier:', error);
      throw error;
    }
  }

  // Supprimer un fichier de Firebase Storage
  async deleteFile(fileUrl: string): Promise<void> {
    try {
      // Extraire le path du fichier depuis l'URL
      const urlObj = new URL(fileUrl);
      const path = decodeURIComponent(urlObj.pathname.split('/o/')[1].split('?')[0]);
      
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
    } catch (error) {
      console.error('Erreur lors de la suppression du fichier:', error);
      // Ne pas throw l'erreur pour ne pas bloquer la suppression du document
    }
  }

  async creerDossier(dossierData: Omit<Dossier, 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'type'>): Promise<string> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }

      const dossier = {
        ...dossierData,
        userId: user.uid,
        type: 'dossier' as const,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, this.dossiersCollection), dossier);
      return docRef.id;
    } catch (error) {
      console.error('Erreur lors de la création du dossier:', error);
      throw error;
    }
  }

  async getDossiersByBien(bienId: string, categorie?: 'gestioncourante' | 'travaux'): Promise<Dossier[]> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }

      let q = query(
        collection(db, this.dossiersCollection),
        where('bienId', '==', bienId),
        where('userId', '==', user.uid)
      );

      if (categorie) {
        q = query(q, where('categorie', '==', categorie));
      }

      const querySnapshot = await getDocs(q);
      const dossiers: Dossier[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        dossiers.push({
          id: doc.id,
          nom: data.nom,
          description: data.description,
          parentId: data.parentId,
          bienId: data.bienId,
          userId: data.userId,
          type: data.type,
          categorie: data.categorie,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          icone: data.icone,
          couleur: data.couleur
        });
      });

      return dossiers.sort((a, b) => a.nom.localeCompare(b.nom));
    } catch (error) {
      console.error('Erreur lors de la récupération des dossiers:', error);
      throw error;
    }
  }

  async supprimerDossier(dossierId: string): Promise<void> {
    try {
      // Supprimer d'abord tous les sous-dossiers
      const sousDossiers = await this.getSousDossiers(dossierId);
      for (const sousDossier of sousDossiers) {
        await this.supprimerDossier(sousDossier.id!);
      }

      // Supprimer tous les documents du dossier
      const documents = await this.getDocumentsByDossier(dossierId);
      for (const document of documents) {
        await this.supprimerDocument(document.id!);
      }

      // Supprimer le dossier lui-même
      await deleteDoc(doc(db, this.dossiersCollection, dossierId));
    } catch (error) {
      console.error('Erreur lors de la suppression du dossier:', error);
      throw error;
    }
  }

  async modifierDossier(dossierId: string, updates: { nom?: string; description?: string }): Promise<void> {
    try {
      const docRef = doc(db, this.dossiersCollection, dossierId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Erreur lors de la modification du dossier:', error);
      throw error;
    }
  }

  async getSousDossiers(parentId: string): Promise<Dossier[]> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }

      const q = query(
        collection(db, this.dossiersCollection),
        where('parentId', '==', parentId),
        where('userId', '==', user.uid)
      );

      const querySnapshot = await getDocs(q);
      const dossiers: Dossier[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        dossiers.push({
          id: doc.id,
          nom: data.nom,
          description: data.description,
          parentId: data.parentId,
          bienId: data.bienId,
          userId: data.userId,
          type: data.type,
          categorie: data.categorie,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          icone: data.icone,
          couleur: data.couleur
        });
      });

      return dossiers;
    } catch (error) {
      console.error('Erreur lors de la récupération des sous-dossiers:', error);
      throw error;
    }
  }

  async ajouterDocument(documentData: Omit<DocumentItem, 'id' | 'createdAt' | 'updatedAt' | 'type'>): Promise<string> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }

      // Si dossierId est null, utiliser le format mobile "root_{bienId}"
      const dossierId = documentData.dossierId || `root_${documentData.bienId}`;

      // Format compatible avec l'application mobile
      const document: any = {
        nom: documentData.nom,
        url: documentData.url,
        dossierId: dossierId,
        bienId: documentData.bienId,
        userId: user.uid,
        // Utiliser le format mobile (fileType, fileSize, fileName)
        fileType: documentData.fileType || documentData.mimeType || 'application/octet-stream',
        fileSize: documentData.fileSize || documentData.taille || 0,
        fileName: documentData.fileName || documentData.nom,
        type: 'document' as const,
        categorie: documentData.categorie,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      // Ajouter dateduDocument seulement si elle existe
      if (documentData.dateduDocument) {
        document.dateduDocument = Timestamp.fromDate(documentData.dateduDocument);
      }

      const docRef = await addDoc(collection(db, this.documentsCollection), document);
      return docRef.id;
    } catch (error) {
      console.error('Erreur lors de l\'ajout du document:', error);
      throw error;
    }
  }

  async getDocumentsByDossier(dossierId: string | null, bienId?: string): Promise<DocumentItem[]> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }

      let q = query(
        collection(db, this.documentsCollection),
        where('dossierId', '==', dossierId),
        where('userId', '==', user.uid)
      );

      if (bienId) {
        q = query(q, where('bienId', '==', bienId));
      }

      const querySnapshot = await getDocs(q);
      const documents: DocumentItem[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        documents.push({
          id: doc.id,
          nom: data.nom,
          url: data.url || data.fileUrl || '',
          dossierId: data.dossierId,
          bienId: data.bienId,
          userId: data.userId,
          // Support des deux formats (mobile et web)
          mimeType: data.mimeType || data.fileType,
          fileType: data.fileType || data.mimeType,
          taille: data.taille || data.fileSize || 0,
          fileSize: data.fileSize || data.taille || 0,
          fileName: data.fileName || data.nom,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          type: 'document',
          categorie: data.categorie,
          dateduDocument: data.dateduDocument?.toDate()
        });
      });

      return documents.sort((a, b) => {
        const dateA = a.dateduDocument || a.createdAt!;
        const dateB = b.dateduDocument || b.createdAt!;
        return dateB.getTime() - dateA.getTime();
      });
    } catch (error) {
      console.error('Erreur lors de la récupération des documents:', error);
      throw error;
    }
  }

  async supprimerDocument(documentId: string): Promise<void> {
    try {
      // Récupérer le document pour obtenir l'URL du fichier
      const docSnap = await getDocs(query(collection(db, this.documentsCollection), where('__name__', '==', documentId)));
      
      if (!docSnap.empty) {
        const docData = docSnap.docs[0].data();
        if (docData.url) {
          // Supprimer le fichier de Storage
          await this.deleteFile(docData.url);
        }
      }

      // Supprimer le document de Firestore
      await deleteDoc(doc(db, this.documentsCollection, documentId));
    } catch (error) {
      console.error('Erreur lors de la suppression du document:', error);
      throw error;
    }
  }

  async renommerDocument(documentId: string, nom: string): Promise<void> {
    try {
      const docRef = doc(db, this.documentsCollection, documentId);
      await updateDoc(docRef, {
        nom,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Erreur lors du renommage du document:', error);
      throw error;
    }
  }

  async modifierDocument(documentId: string, data: { nom?: string; dateduDocument?: Date }): Promise<void> {
    try {
      const docRef = doc(db, this.documentsCollection, documentId);
      const updateData: any = {
        updatedAt: Timestamp.now()
      };
      if (data.nom !== undefined) {
        updateData.nom = data.nom;
      }
      if (data.dateduDocument !== undefined) {
        updateData.dateduDocument = Timestamp.fromDate(data.dateduDocument);
      }
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Erreur lors de la modification du document:', error);
      throw error;
    }
  }

  async getDocumentsByBien(bienId: string, categorie?: string): Promise<DocumentItem[]> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }


      let q = query(
        collection(db, this.documentsCollection),
        where('bienId', '==', bienId),
        where('userId', '==', user.uid)
      );

      if (categorie) {
        q = query(q, where('categorie', '==', categorie));
      }

      const querySnapshot = await getDocs(q);
      const documents: DocumentItem[] = [];


      querySnapshot.forEach((doc) => {
        const data = doc.data();
        documents.push({
          id: doc.id,
          nom: data.nom,
          url: data.url || data.fileUrl || '',
          dossierId: data.dossierId,
          bienId: data.bienId,
          userId: data.userId,
          // Support des deux formats (mobile et web)
          mimeType: data.mimeType || data.fileType,
          fileType: data.fileType || data.mimeType,
          taille: data.taille || data.fileSize || 0,
          fileSize: data.fileSize || data.taille || 0,
          fileName: data.fileName || data.nom,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          type: 'document',
          categorie: data.categorie,
          dateduDocument: data.dateduDocument?.toDate()
        });
      });

      return documents.sort((a, b) => {
        const dateA = a.dateduDocument || a.createdAt!;
        const dateB = b.dateduDocument || b.createdAt!;
        return dateB.getTime() - dateA.getTime();
      });
    } catch (error) {
      console.error('Erreur lors de la récupération des documents:', error);
      throw error;
    }
  }
}

export const dossiersService = new DossiersService();